import { useMediaQuery, useTheme } from "@mui/material";
import { useCallback, useState } from "react";

import { SCOUT_INFO_PANEL_TABS, type ScoutInfoPanelTab } from "./scoutInfoPanelContent";

export type ScoutSidePanelView = "search" | "home" | ScoutInfoPanelTab;

const DEFAULT_VIEW: Exclude<ScoutSidePanelView, ScoutInfoPanelTab> = "home";

function isInfoView(view: ScoutSidePanelView): view is ScoutInfoPanelTab {
  return SCOUT_INFO_PANEL_TABS.some((tab) => tab.value === view);
}

/**
 * Manages Scout's right-side icon rail and drawer across breakpoints.
 *
 * - Closed by default; the rail stays visible with icon shortcuts.
 * - `sm` and up: drawer sits beside the chat when open (persistent layout).
 * - Below `sm` (mobile): drawer opens as a temporary fullscreen overlay.
 *
 * The drawer shows home (recents) or search as the primary surface. Footer chips
 * open a slide-up sheet for Sources / Method / Privacy & Data without leaving
 * that surface. Opening search (or focusing the search field) dismisses the
 * sheet.
 *
 * Closing always resets to the home zero state, so the panel reopens fresh
 * (search field + unselected tabs + recents) rather than to whatever the user
 * was last looking at.
 */
export function useScoutInfoPanel() {
  const theme = useTheme();
  const isPersistentMode = useMediaQuery(theme.breakpoints.up("sm"));
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Exclude<ScoutSidePanelView, ScoutInfoPanelTab>>(DEFAULT_VIEW);
  const [tab, setTab] = useState<ScoutInfoPanelTab | null>(null);

  const openToView = useCallback((nextView: ScoutSidePanelView) => {
    if (isInfoView(nextView)) {
      setTab(nextView);
      // Keep search if already searching; otherwise show recents under the sheet.
      setView((prev) => (prev === "search" ? "search" : "home"));
      setOpen(true);
      return;
    }

    setView(nextView);
    // Search and home are full surfaces — dismiss any open info sheet.
    setTab(null);
    setOpen(true);
  }, []);

  const changeTab = useCallback((nextTab: ScoutInfoPanelTab) => {
    // Toggle off when clicking the already-selected chip.
    setTab((current) => (current === nextTab ? null : nextTab));
  }, []);

  const clearTab = useCallback(() => {
    setTab(null);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    setView(DEFAULT_VIEW);
    setTab(null);
  }, []);

  const togglePanel = useCallback(() => {
    if (open) {
      closePanel();
      return;
    }
    setOpen(true);
  }, [closePanel, open]);

  return {
    open,
    view,
    tab,
    changeTab,
    clearTab,
    isPersistentMode,
    openToView,
    togglePanel,
    closePanel,
  };
}
